import { useEffect, useRef, useState } from "react";
import api, { formatApiErrorDetail, BACKEND_URL } from "../../lib/api";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { toast } from "sonner";
import { UploadCloud, Film } from "lucide-react";

const resolveVideo = (url) => (url && url.startsWith("/api") ? `${BACKEND_URL}${url}` : url);

/**
 * One-modal flow: drop a video → preview + pick artist → publish.
 *
 * Used by the floating admin button on the homepage. The single screen
 * absorbs what used to take 3 separate fields (artist select + URL input
 * + title + add) into a guided two-step view.
 */
export const QuickPublishVideoDialog = ({ open, onClose, onPublished }) => {
  const [uploading, setUploading] = useState(false);
  const [videoUrl, setVideoUrl] = useState("");      // /api/files/<id> after upload OR pasted YT/Vimeo URL
  const [videoMeta, setVideoMeta] = useState(null);  // { name, size } when uploaded
  const [pastedUrl, setPastedUrl] = useState("");
  const [artists, setArtists] = useState([]);
  const [artistId, setArtistId] = useState("");
  const [title, setTitle] = useState("");
  const [publishing, setPublishing] = useState(false);
  const fileRef = useRef(null);

  // Reset state on each open so a stale draft doesn't linger.
  useEffect(() => {
    if (!open) return;
    setVideoUrl("");
    setVideoMeta(null);
    setPastedUrl("");
    setArtistId("");
    setTitle("");
  }, [open]);

  useEffect(() => {
    if (!open) return;
    api
      .get("/admin/users", { params: { role: "artist", limit: 200 } })
      .then(({ data }) =>
        setArtists((data.items || []).filter((u) => !u.deleted_at)),
      )
      .catch(() => {});
  }, [open]);

  const handleFileSelected = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const data = new FormData();
      data.append("file", file);
      const { data: r } = await api.post("/upload/video", data, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setVideoUrl(r.url);
      setVideoMeta({ name: file.name, size: file.size });
      toast.success("Video uploaded · pick an artist below");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const useUrl = () => {
    if (!pastedUrl.trim()) {
      toast.error("Paste a YouTube or Vimeo URL first");
      return;
    }
    if (!/^https?:\/\//i.test(pastedUrl.trim())) {
      toast.error("URL must start with https://");
      return;
    }
    setVideoUrl(pastedUrl.trim());
    setVideoMeta({ name: "External URL", size: 0 });
  };

  const isSelfHostedVideo = videoUrl && videoUrl.startsWith("/api/files/");

  const publish = async () => {
    if (!videoUrl) {
      toast.error("Upload a video or paste a URL first");
      return;
    }
    if (!artistId) {
      toast.error("Pick the artist to credit");
      return;
    }
    setPublishing(true);
    try {
      const payload = {
        artist_id: artistId,
        video_url: videoUrl,
        is_featured: true,
      };
      if (title.trim()) payload.title = title.trim();
      await api.post("/admin/intro-videos", payload);
      toast.success("Live on the homepage ✨");
      onPublished?.();
      onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not publish");
    } finally {
      setPublishing(false);
    }
  };

  const selectedArtist = artists.find((a) => a.id === artistId);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        data-testid="quick-publish-video-dialog"
        className="rounded-none border border-[#E5E0D8] bg-white max-w-2xl max-h-[92vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="font-serif-display text-3xl text-[#111] font-light">
            Publish a video to the homepage
          </DialogTitle>
          <DialogDescription className="text-sm text-[#555]">
            Upload a clip or paste a YouTube / Vimeo link, credit an artist, and it goes live in the &ldquo;Meet the masters&rdquo; gallery.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-2 space-y-6">
          {!videoUrl ? (
            <>
              {/* Step 1: file drop or URL paste */}
              <label
                htmlFor="quick-video-file"
                data-testid="quick-publish-drop"
                className="block cursor-pointer border-2 border-dashed border-[#1E3A8A]/40 bg-[#F4EFE6]/40 hover:bg-[#F4EFE6]/70 transition-colors p-10 text-center"
              >
                <UploadCloud className="w-10 h-10 text-[#1E3A8A] mx-auto" strokeWidth={1.5} />
                <p className="font-serif-display text-2xl mt-4 text-[#111]">
                  {uploading ? "Uploading…" : "Click to choose a video"}
                </p>
                <p className="text-sm text-[#555] mt-2">
                  MP4 / WebM / MOV / M4V · up to 100&nbsp;MB
                </p>
                <input
                  ref={fileRef}
                  id="quick-video-file"
                  data-testid="quick-publish-file"
                  type="file"
                  accept="video/mp4,video/webm,video/quicktime,video/x-m4v"
                  onChange={handleFileSelected}
                  disabled={uploading}
                  className="hidden"
                />
              </label>
              <div className="relative flex items-center gap-3">
                <span className="flex-1 h-px bg-[#E5E0D8]" />
                <span className="label-eyebrow text-[#555]">or paste a link</span>
                <span className="flex-1 h-px bg-[#E5E0D8]" />
              </div>
              <div className="flex gap-2">
                <Input
                  data-testid="quick-publish-url-input"
                  value={pastedUrl}
                  onChange={(e) => setPastedUrl(e.target.value)}
                  placeholder="https://youtube.com/watch?v=… or https://vimeo.com/…"
                  className="rounded-none border-b-2 border-l-0 border-r-0 border-t-0 border-[#E5E0D8] bg-transparent px-0 focus-visible:ring-0 focus-visible:border-[#111]"
                />
                <Button
                  type="button"
                  onClick={useUrl}
                  data-testid="quick-publish-url-use"
                  variant="outline"
                  className="rounded-none border-[#1E3A8A] text-[#1E3A8A] label-eyebrow"
                >
                  Use URL
                </Button>
              </div>
            </>
          ) : (
            <>
              {/* Step 2: preview + credit */}
              <div data-testid="quick-publish-preview" className="aspect-video bg-black border border-[#E5E0D8]">
                {isSelfHostedVideo ? (
                  <video
                    src={resolveVideo(videoUrl)}
                    controls
                    muted
                    playsInline
                    className="w-full h-full object-cover"
                  >
                    <track kind="captions" />
                  </video>
                ) : (
                  <iframe
                    src={videoUrl}
                    title="Video preview"
                    className="w-full h-full"
                    allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                )}
              </div>
              <div className="flex items-center justify-between gap-3 text-sm text-[#555]">
                <span className="flex items-center gap-2 truncate">
                  <Film className="w-3.5 h-3.5 text-[#1E3A8A] flex-shrink-0" />
                  <span className="truncate">{videoMeta?.name || videoUrl}</span>
                </span>
                <button
                  type="button"
                  onClick={() => {
                    setVideoUrl("");
                    setVideoMeta(null);
                  }}
                  data-testid="quick-publish-replace"
                  className="label-eyebrow text-[#1E3A8A] hover:text-[#D32F2F] flex-shrink-0"
                >
                  Replace
                </button>
              </div>

              <div>
                <label className="label-eyebrow text-[#555]">Credit this video to *</label>
                <Select value={artistId} onValueChange={setArtistId}>
                  <SelectTrigger
                    data-testid="quick-publish-artist-select"
                    className="mt-2 rounded-none border-b-2 border-l-0 border-r-0 border-t-0 border-[#E5E0D8] bg-transparent px-0 focus:ring-0"
                  >
                    <SelectValue placeholder="Pick the artist…" />
                  </SelectTrigger>
                  <SelectContent className="rounded-none max-h-[300px]">
                    {artists.map((a) => (
                      <SelectItem
                        key={a.id}
                        value={a.id}
                        data-testid={`quick-publish-artist-option-${a.id}`}
                      >
                        {a.name}{" "}
                        {a.publish_status === "published"
                          ? "· ✓ public"
                          : "· (draft profile — section will hide it until published)"}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedArtist && selectedArtist.publish_status !== "published" && (
                  <p
                    data-testid="quick-publish-unpublished-warning"
                    className="text-xs text-[#A87900] mt-2"
                  >
                    Heads up — {selectedArtist.name}&apos;s profile is still in draft, so the homepage section will hide this video until you publish their profile.
                  </p>
                )}
              </div>

              <div>
                <label className="label-eyebrow text-[#555]">Headline (optional)</label>
                <Input
                  data-testid="quick-publish-title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Defaults to the artist's name"
                  className="mt-2 rounded-none border-b-2 border-l-0 border-r-0 border-t-0 border-[#E5E0D8] bg-transparent px-0 py-2 focus-visible:ring-0 focus-visible:border-[#111]"
                />
              </div>
            </>
          )}
        </div>

        <DialogFooter className="pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            className="rounded-none border-[#E5E0D8] text-[#555] hover:text-[#111] px-5 py-3 h-auto label-eyebrow"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={publish}
            data-testid="quick-publish-submit"
            disabled={!videoUrl || !artistId || publishing}
            className="rounded-none bg-[#1E3A8A] hover:bg-[#162a66] text-white px-6 py-3 h-auto label-eyebrow disabled:opacity-40"
          >
            {publishing ? "Publishing…" : "Publish to homepage"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
